<template>
    <div>
        <v-form ref="folderForm" v-model="isFormValid">
            <v-row v-for="language in availableLanguages" :key="language.code">
                <v-col cols="12">
                    <v-text-field v-model="folderTranslations[language.code]"
                        :label="$t('Kansion nimi') + ' (' + language.name + ')'" outlined hide-details="auto">
                        <template v-slot:prepend>
                            <img :src="language.icon" style="max-width: 30px" />
                        </template>
                    </v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-text-field v-model.number="uiOrder" :label="$t('Järjestys')" outlined hide-details="auto"
                        type="number" min="0" step="1">
                    </v-text-field>
                </v-col>
            </v-row>
        </v-form>
        <v-alert type="error" class="mt-2" v-if="errors">
            {{ errors }}
        </v-alert>
        <v-card-actions class="mt-4">
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="cancel">
                {{ $t('Peruuta') }}
            </v-btn>
            <v-btn v-if="mode === 'edit'" color="error" @click="deleteFolder" :loading="isDeleting">
                {{ $t('Poista') }}
            </v-btn>
            <v-btn color="primary" @click="save" :loading="isSaving" :disabled="!isFormValid">
                {{ $t('Tallenna') }}
            </v-btn>
        </v-card-actions>
    </div>
</template>

<script>
import { ref, watch, computed, onMounted } from "@vue/composition-api";
import adminApi from "@/api/admin";
import { useTranslations } from "@/@core/libs/i18n";
import useAuth from "@/useAuth";
import { required } from "@/@core/utils/validation";
import { i18n } from "@/plugins/i18n";

export default {
    name: "FolderForm",
    props: {
        folder: {
            type: Object,
            default: null
        },
        mode: {
            type: String,
            default: 'create', // 'create' or 'edit'
            validator: value => ['create', 'edit'].includes(value)
        },
        initialTitle: {
            type: String,
            default: ''
        },
        parentFolderId: {
            type: Number,
            default: null
        }
    },
    setup(props, { emit }) {
        const { t } = useTranslations();
        const { tenant } = useAuth();
        const folderTranslations = ref({});
        const uiOrder = ref(null);
        const isSaving = ref(false);
        const isFormValid = ref(false);
        const folderForm = ref(null);
        const isDeleting = ref(false);
        const errors = ref("");

        // Get available languages from tenant
        const availableLanguages = computed(() => {
            return tenant.value?.languages || [];
        });

        // Initialize translations when component mounts or folder changes
        const initializeTranslations = () => {
            folderTranslations.value = {};
            errors.value = ""; // Clear any previous errors

            if (props.mode === 'edit' && props.folder) {
                // Editing existing folder - populate with existing translations
                availableLanguages.value.forEach(lang => {
                    const existingTranslation = props.folder.translations?.[lang.code];
                    folderTranslations.value[lang.code] = existingTranslation?.title || '';
                });
                // Set ui_order from folder
                uiOrder.value = props.folder.ui_order ?? null;
            } else {
                // Creating new folder - initialize with initial title for primary language
                availableLanguages.value.forEach(lang => {
                    if (lang.code === i18n.locale) {
                        folderTranslations.value[lang.code] = props.initialTitle;
                    }
                    else {
                        folderTranslations.value[lang.code] = '';
                    }
                });
                // Reset ui_order for new folder
                uiOrder.value = null;
            }
        };

        // Watch for changes in folder prop
        watch(() => props.folder, () => {
            initializeTranslations();
        }, { immediate: true });

        // Watch for changes in mode
        watch(() => props.mode, () => {
            initializeTranslations();
        });

        const cancel = () => {
            folderTranslations.value = {};
            emit('cancel');
        };

        const deleteFolder = async () => {
            errors.value = "";
            if (props.mode === 'edit' && props.folder) {
                if (confirm(i18n.t('Haluatko varmasti poistaa kansion?'))) {
                    isDeleting.value = true;
                    const res = await adminApi.deleteResourceFolder(props.folder.id);
                    if (res.ok) {
                        emit('delete');
                    } else {
                        errors.value = res.data;
                    }
                    isDeleting.value = false;
                }
            }
        }

        const save = async () => {
            errors.value = "";

            // Check if at least one translation is provided
            const hasAtLeastOneTranslation = Object.keys(folderTranslations.value).some(langCode => {
                const translation = folderTranslations.value[langCode];
                return translation && translation.trim().length > 0;
            });

            if (!hasAtLeastOneTranslation) {
                errors.value = i18n.t('Anna kansion nimi');
                return;
            }

            // Validate the form before proceeding
            if (!folderForm.value.validate()) {
                return;
            }

            isSaving.value = true;
            try {
                // Prepare translations object with only non-empty values
                const translations = {};
                Object.keys(folderTranslations.value).forEach(langCode => {
                    const translation = folderTranslations.value[langCode];
                    if (translation && translation.trim().length > 0) {
                        translations[langCode] = { title: translation.trim() };
                    }
                });

                let response;
                const folderData = { translations };

                // Include ui_order if provided (null/undefined will be sent if not set)
                if (uiOrder.value !== null && uiOrder.value !== undefined && uiOrder.value !== '') {
                    folderData.ui_order = uiOrder.value;
                }

                // Include parent folder ID if creating a subfolder
                if (props.parentFolderId) {
                    folderData.parent = props.parentFolderId;
                }

                if (props.mode === 'edit' && props.folder) {
                    // Include ID for update
                    folderData.id = props.folder.id;
                    // Update existing folder
                }

                response = await adminApi.saveResourceFolder(folderData);

                if (response.ok) {
                    emit('save', response.data);
                    folderTranslations.value = {};
                } else {
                    errors.value = response.error || response.data;
                }
            } catch (error) {
                errors.value = error.message || String(error);
            } finally {
                isSaving.value = false;
            }
        };

        return {
            folderTranslations,
            uiOrder,
            isSaving,
            availableLanguages,
            isFormValid,
            folderForm,
            required,
            deleteFolder,
            isDeleting,
            cancel,
            save,
            errors
        };
    }
};
</script>
